import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Drawer,
  Empty,
  Form,
  Image,
  Descriptions,
  Input,
  Modal,
  List,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  AuditOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EllipsisOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CopyableText from "components/common/CopyableText";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { useAuth } from "contexts/AuthContext";
import olreadiaService from "services/olreadiaService";
import apiClient from "utils/apiClient";
import useBackgroundChecks from "hooks/olreadia/useBackgroundChecks";
import { useUserManagement } from "hooks";
import paths from "routes/paths";

const { Text } = Typography;

const BACKGROUND_CHECK_TYPES = [
  {
    value: "Profile validation and verification",
    label: "Profile validation and verification",
  },
  { value: "Criminal record checks", label: "Criminal record checks" },
];

const BACKGROUND_CHECK_STATUSES = [
  { value: "REQUESTED", label: "Requested" },
  { value: "PENDING", label: "Pending" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "PASSED", label: "Passed" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELED", label: "Canceled" },
];

const getVerificationItemSnapshotSource = (record) =>
  record?.snapshot ??
  record?.currentItem ??
  record?.current_item ??
  record?.expectedSnapshot ??
  record?.expected_snapshot ??
  record?.valueSnapshot ??
  record?.value_snapshot ??
  null;

const getVerificationItemFoundValue = (record) =>
  record?.valueFound ??
  record?.value_found ??
  record?.foundValue ??
  record?.found_value ??
  record?.observedValue ??
  record?.observed_value ??
  record?.resultValue ??
  record?.result_value ??
  null;

const normalizeSnapshotPayload = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const toLabel = (value) =>
  value
    ? String(value)
        .replaceAll("_", " ")
        .replace(/\b\w/g, (match) => match.toUpperCase())
    : "-";

const formatFieldLabel = (value) => {
  if (!value) return "Not specified";
  const lastSegment = String(value).split(".").filter(Boolean).pop() || String(value);
  return lastSegment
    .replace(/\[(\d+)\]/g, " $1")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const FILE_ID_PATTERN = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;

const extractFileId = (value) => {
  if (!value || typeof value !== "string") return null;
  return value.match(FILE_ID_PATTERN)?.[0] || null;
};

const extractLastFileId = (value) => {
  if (!value || typeof value !== "string") return null;
  const matches = value.match(new RegExp(FILE_ID_PATTERN.source, "g"));
  return matches?.[matches.length - 1] || null;
};

const normalizeFileId = (value) => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  return new RegExp(`^${FILE_ID_PATTERN.source}$`).test(trimmed)
    ? trimmed
    : extractLastFileId(trimmed);
};

const toStableFileValue = (value) => extractFileId(value) || value || null;

const sanitizeSnapshotValue = (value) => {
  if (typeof value === "string" && /^(https?|blob|file):/i.test(value)) {
    return extractFileId(value) || "File available";
  }
  if (Array.isArray(value)) return value.map(sanitizeSnapshotValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !/(^|_)(signed)?url$/i.test(key) && !/Url$/.test(key))
        .map(([key, childValue]) => [key, sanitizeSnapshotValue(childValue)]),
    );
  }
  return value;
};

const parseEmbeddedFileReferences = (value) => {
  if (!value || typeof value !== "string") return [];
  const pattern = new RegExp(
    `Id:\\s*(${FILE_ID_PATTERN.source})[^\\r\\n]*?Caption:\\s*([^\\r\\n]*?)\\s*Url:\\s*(https?:\\/\\/\\S+)`,
    "gi",
  );
  return Array.from(value.matchAll(pattern), (match) => ({
    fileId: match[1],
    caption: match[2]
      .replace(/\s*(?:\u2022|\u00b7|\u00e2\u20ac\u00a2)\s*$/u, "")
      .trim(),
    legacyUrl: match[3],
  }));
};

const getVerificationCategory = (record) => {
  const value = `${record?.itemType || ""} ${record?.propertyPath || ""}`.toLowerCase();
  if (/address|residen|region|location/.test(value)) return "Residence History";
  if (/work|experience|employ|organization|role/.test(value)) return "Work History";
  if (/education|school|institution|program|graduation/.test(value)) return "Education";
  if (/certification|document|identity|photo/.test(value)) return "Documents & Identity";
  if (/reference/.test(value)) return "References";
  return "Profile Details";
};

const getSectionIcon = (category) => {
  if (category === "Identity" || category === "Documents & Identity") return <IdcardOutlined />;
  if (category === "Residence History") return <EnvironmentOutlined />;
  if (category === "Work History") return <AuditOutlined />;
  return <SafetyCertificateOutlined />;
};

const detailDescriptionProps = {
  size: "small",
  column: { xs: 1, sm: 2 },
  colon: false,
  labelStyle: { color: "#64748b", fontSize: 12, fontWeight: 600, paddingBottom: 4 },
  contentStyle: { color: "#0f172a", fontSize: 13, fontWeight: 500, paddingBottom: 14 },
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : String(value);
};

const getVerificationItemStatus = (record) => String(record?.status || "").toUpperCase();

const getVerificationItemStateLabel = (record) => {
  const status = getVerificationItemStatus(record);
  const foundValue = getVerificationItemFoundValue(record);
  if (status === "PASS" || status === "PASSED") return "Verified";
  if (status === "FAIL" || status === "FAILED") return "Failed";
  if (foundValue) return "Captured";
  return "Pending";
};

const getVerificationItemStateColor = (record) => {
  const status = getVerificationItemStatus(record);
  const foundValue = getVerificationItemFoundValue(record);
  if (status === "PASS" || status === "PASSED") return "green";
  if (status === "FAIL" || status === "FAILED") return "volcano";
  if (foundValue) return "blue";
  return "gold";
};

const getVerificationItemAccent = (record) => {
  const color = getVerificationItemStateColor(record);
  if (color === "green") {
    return {
      borderColor: "#22c55e",
      background: "#f0fdf4",
      shadow: "0 8px 22px rgba(34, 197, 94, 0.12)",
      softBackground: "#ecfdf5",
      softBorder: "#86efac",
    };
  }
  if (color === "volcano") {
    return {
      borderColor: "#f97316",
      background: "#fff7ed",
      shadow: "0 8px 22px rgba(249, 115, 22, 0.12)",
      softBackground: "#fff7ed",
      softBorder: "#fdba74",
    };
  }
  if (color === "blue") {
    return {
      borderColor: "#3b82f6",
      background: "#eff6ff",
      shadow: "0 8px 22px rgba(59, 130, 246, 0.12)",
      softBackground: "#eff6ff",
      softBorder: "#93c5fd",
    };
  }
  return {
    borderColor: "#f59e0b",
    background: "#fffbeb",
    shadow: "0 8px 22px rgba(245, 158, 11, 0.12)",
    softBackground: "#fff7d6",
    softBorder: "#fcd34d",
  };
};

const getVerificationItemKey = (record, index = null) =>
  [
    record?.propertyPath || "",
    record?.itemType || "",
    index !== null ? String(index) : "",
  ]
    .filter(Boolean)
    .join("|");

const buildVerificationItemFormValues = (record = null) => ({
  itemType: record?.itemType || null,
  propertyPath: record?.propertyPath || "",
  currentItem: normalizeSnapshotPayload(
    getVerificationItemSnapshotSource(record) || record?.currentItem || "",
  ),
  valueFound: getVerificationItemFoundValue(record) || "",
  matchResult: record?.matchResult,
  status: record?.status,
  required: typeof record?.required === "boolean" ? record.required : true,
});

const tryParseJsonValue = (value) => {
  if (value == null || value === "") return null;
  if (Array.isArray(value) || typeof value === "object") return value;
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (
    !(
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    )
  ) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const formatSnapshotDateValue = (value) => {
  if (value == null || value === "") return "Not provided";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(value);
};

const formatSnapshotPrimitiveValue = (value) => {
  if (value == null || value === "") return "Not provided";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const resolveSnapshotPathValue = (source, path) => {
  if (!source || !path) return null;

  const tokens = String(path)
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .map((token) => token.trim())
    .filter(Boolean);

  let current = source;
  for (const token of tokens) {
    if (current == null) return null;
    current = current[token];
  }

  return current;
};

const renderSnapshotFieldValue = (value) => {
  if (value == null || value === "") {
    return <Text type="secondary">Not provided</Text>;
  }
  if (typeof value === "boolean") {
    return <Tag>{value ? "Yes" : "No"}</Tag>;
  }
  const embeddedFiles = parseEmbeddedFileReferences(value);
  if (embeddedFiles.length) {
    return (
      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        {embeddedFiles.map((file) => (
          <SnapshotFilePreview
            key={file.fileId}
            fileId={file.fileId}
            caption={file.caption}
            hintedUrl={file.legacyUrl}
          />
        ))}
      </Space>
    );
  }
  if (typeof value === "object") {
    return <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(sanitizeSnapshotValue(value), null, 2)}</Text>;
  }
  return <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{String(sanitizeSnapshotValue(value))}</Text>;
};

const FileEvidence = ({ fileId, signedUrl, label = "File" }) => {
  const stableFileId = normalizeFileId(fileId);
  if (!stableFileId) return null;
  return <SnapshotFilePreview fileId={stableFileId} caption={label} hintedUrl={signedUrl} />;
};

const SnapshotFilePreview = ({ fileId, caption, hintedUrl }) => {
  const stableFileId = normalizeFileId(fileId) || extractLastFileId(hintedUrl);
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const loadSignedUrl = async () => {
      if (!stableFileId) return;
      setLoading(true);
      try {
        const response = await apiClient.get(
          `/storage-service/api/v1/files/photo/signed-urls?fids=${encodeURIComponent(stableFileId)}`,
        );
        const data = response?.data;
        const resolvedUrl =
          (typeof data === "string" && data.trim()) ||
          (Array.isArray(data) && (typeof data[0] === "string" ? data[0] : data[0]?.url || data[0]?.signedUrl)) ||
          (Array.isArray(data?.signedUrls) && (typeof data.signedUrls[0] === "string" ? data.signedUrls[0] : data.signedUrls[0]?.url || data.signedUrls[0]?.signedUrl)) ||
          (typeof data?.url === "string" && data.url) ||
          (typeof data?.signedUrl === "string" && data.signedUrl) ||
          (typeof data?.[stableFileId] === "string" && data[stableFileId]) ||
          null;
        if (mounted) setSignedUrl(resolvedUrl);
      } catch {
        if (mounted) setSignedUrl(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadSignedUrl();
    return () => {
      mounted = false;
    };
  }, [stableFileId]);

  if (!stableFileId) return null;
  const isPdf = /\.pdf(?:\?|$)/i.test(String(hintedUrl || signedUrl || ""));
  return (
    <Card size="small" loading={loading} style={{ borderRadius: 10 }} styles={{ body: { padding: 10 } }}>
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Text strong>{caption || "Document"}</Text>
        {signedUrl && !isPdf ? (
          <Image
            src={signedUrl}
            alt={caption || "Document preview"}
            style={{ width: "100%", maxHeight: 260, objectFit: "contain", borderRadius: 8 }}
          />
        ) : signedUrl ? (
          <Button href={signedUrl} target="_blank" rel="noreferrer">Open document</Button>
        ) : (
          <Text type="secondary">Preview unavailable</Text>
        )}
      </Space>
    </Card>
  );
};

const SNAPSHOT_SECTIONS = [
  {
    key: "identity",
    title: "Identity",
    fields: [
      ["Profile name", "profileName"],
      ["Profile type", "profileType"],
      ["Profile summary", "profileSummary"],
      ["User ID", "userId"],
      ["Profession profile ID", "id"],
    ],
  },
  {
    key: "classification",
    title: "Classification",
    fields: [
      ["Sector", "sector"],
      ["Sectors", "sectors"],
      ["Education level", "educationLevel"],
      ["Experience", "experience"],
    ],
  },
  {
    key: "residence",
    title: "Residence History checks",
    arrayPaths: ["residenceHistory", "addressHistory", "previousRegionsWorkedIn"],
    emptyText: "No residence history entries",
  },
  {
    key: "work",
    title: "Work History checks",
    arrayPath: "workExperienceHistory",
    emptyText: "No work history entries",
  },
  {
    key: "education",
    title: "Education",
    arrayPath: "education",
    emptyText: "No education entries",
  },
  {
    key: "certifications",
    title: "Certifications",
    arrayPath: "certifications",
    emptyText: "No certifications",
  },
  {
    key: "documents",
    title: "Documents",
    arrayPath: "documents",
    emptyText: "No documents",
  },
  {
    key: "photos",
    title: "Photos",
    arrayPath: "photos",
    emptyText: "No photos",
  },
  {
    key: "references",
    title: "References",
    arrayPath: "references",
    emptyText: "No references",
  },
  {
    key: "serviceInterest",
    title: "Service Interest",
    arrayPath: "interestedServices",
    emptyText: "No service interests",
  },
  {
    key: "locationInterest",
    title: "Location Interest",
    arrayPath: "interestedLocations",
    emptyText: "No location interests",
  },
  {
    key: "shiftInterest",
    title: "Shift Interest",
    arrayPath: "interestedShifts",
    emptyText: "No shift interests",
  },
];

const renderSnapshotSectionFields = (source, fields) => {
  const visibleFields = fields
    .map(([label, path]) => [label, resolveSnapshotPathValue(source, path)])
    .filter(([, fieldValue]) => fieldValue !== undefined && fieldValue !== null && fieldValue !== "");

  if (!visibleFields.length) {
    return <Text type="secondary">No data in this section</Text>;
  }

  return (
    <Descriptions
      {...detailDescriptionProps}
      column={1}
      layout="vertical"
    >
      {visibleFields.map(([label, fieldValue]) => (
        <Descriptions.Item key={label} label={label}>
          {renderSnapshotFieldValue(fieldValue)}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
};

const renderSnapshotSectionArray = (value) => {
  const parsed = tryParseJsonValue(value);
  if (!parsed) return <Text type="secondary">No data in this section</Text>;
  if (Array.isArray(parsed)) {
    if (!parsed.length) return <Text type="secondary">No entries found.</Text>;
    return (
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {parsed.map((item, index) => renderSnapshotEntryCard(item, index))}
      </Space>
    );
  }
  if (typeof parsed === "object") {
    return renderSnapshotEntryCard(parsed, 0);
  }
  return renderSnapshotFieldValue(parsed);
};

const renderGroupedSnapshotValue = (value) => {
  const parsed = tryParseJsonValue(value);
  if (!parsed) {
    return <Text type="secondary">No snapshot data</Text>;
  }

  if (Array.isArray(parsed)) {
    return renderSnapshotSectionArray(parsed);
  }

  if (typeof parsed !== "object") {
    return renderSnapshotFieldValue(parsed);
  }

  const source = parsed;
  const sections = SNAPSHOT_SECTIONS.map((section) => {
    if (section.fields) {
      const hasData = section.fields.some(([, path]) => {
        const fieldValue = resolveSnapshotPathValue(source, path);
        return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
      });
      return {
        ...section,
        hasData,
      };
    }

    const sectionValue = section.arrayPaths
      ? section.arrayPaths.flatMap((path) => {
          const pathValue = tryParseJsonValue(resolveSnapshotPathValue(source, path));
          if (pathValue === undefined || pathValue === null || pathValue === "") return [];
          return Array.isArray(pathValue) ? pathValue : [pathValue];
        })
      : resolveSnapshotPathValue(source, section.arrayPath);
    const parsedValue = tryParseJsonValue(sectionValue);
    const hasData = Array.isArray(parsedValue) ? parsedValue.length > 0 : Boolean(parsedValue);
    return {
      ...section,
      hasData,
      value: sectionValue,
    };
  }).filter((section) => section.hasData);

  if (!sections.length) {
    return <Text type="secondary">No snapshot data</Text>;
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {sections.map((section) => (
        <Card
          key={section.key}
          size="small"
          style={{ borderRadius: 14, borderColor: "#e2e8f0", overflow: "hidden" }}
          styles={{ header: { background: "#f8fafc", minHeight: 44 }, body: { padding: 16 } }}
          title={<Text strong style={{ color: "#0f172a" }}>{section.title}</Text>}
        >
          {section.fields
            ? renderSnapshotSectionFields(source, section.fields)
            : renderSnapshotSectionArray(section.value)}
        </Card>
      ))}
    </Space>
  );
};

const renderSnapshotEntryCard = (item, index) => {
  const record = item && typeof item === "object" ? item : { value: item };
  const snapshotFileId = normalizeFileId(record.id || record.fileId || record.file_id || record.url);
  if (snapshotFileId && (record.caption || record.url || record.fileId || record.file_id)) {
    return (
      <SnapshotFilePreview
        key={`${snapshotFileId}-${index}`}
        fileId={snapshotFileId}
        caption={record.caption || record.name || `Document ${index + 1}`}
        hintedUrl={record.url}
      />
    );
  }
  const title = record.organization || record.role || record.name || `Entry ${index + 1}`;
  const isWorkExperienceRow =
    Object.prototype.hasOwnProperty.call(record, "organization") ||
    Object.prototype.hasOwnProperty.call(record, "role") ||
    Object.prototype.hasOwnProperty.call(record, "location") ||
    Object.prototype.hasOwnProperty.call(record, "isCurrent");

  const workExperienceFields = [
        ["Organization", record.organization],
        ["Role", record.role],
        ["Location", record.location],
        ["Start date", record.startDate],
        ["End date", record.endDate],
        ["Current", record.isCurrent],
        ["Notes", record.notes],
      ];
  const workExperienceFieldKeys = new Set([
    "organization",
    "role",
    "location",
    "startDate",
    "endDate",
    "isCurrent",
    "notes",
  ]);
  const fields = isWorkExperienceRow
    ? [
        ...workExperienceFields,
        ...Object.entries(record)
          .filter(([key]) => !workExperienceFieldKeys.has(key))
          .map(([key, fieldValue]) => [formatFieldLabel(key), fieldValue]),
      ]
    : Object.entries(record).map(([key, fieldValue]) => [formatFieldLabel(key), fieldValue]);

  return (
    <Card
      key={`${title}-${index}`}
      size="small"
      styles={{ body: { padding: 12 } }}
      style={{ borderRadius: 12 }}
      title={
        <Space size={8} wrap>
          <Text strong>{title}</Text>
          {typeof record.isCurrent === "boolean" && record.isCurrent ? (
            <Tag>Current</Tag>
          ) : null}
        </Space>
      }
    >
      <Descriptions
        {...detailDescriptionProps}
        column={1}
        layout="vertical"
      >
        {fields
          .filter(([, fieldValue]) => fieldValue !== undefined && fieldValue !== null && fieldValue !== "")
          .map(([label, fieldValue]) => (
            <Descriptions.Item key={label} label={label}>
              {/(notes?|reasons?)/i.test(label) && typeof fieldValue === "string" ? (
                <Tooltip title={fieldValue}>
                  <Text ellipsis style={{ display: "block", maxWidth: "100%" }}>
                    {fieldValue}
                  </Text>
                </Tooltip>
              ) : label.toLowerCase().includes("date") ? (
                formatSnapshotDateValue(fieldValue)
              ) : (
                renderSnapshotFieldValue(fieldValue)
              )}
            </Descriptions.Item>
          ))}
      </Descriptions>
    </Card>
  );
};

const renderStructuredSnapshotValue = (value) => {
  return renderGroupedSnapshotValue(value);
};

const countChecksByStatus = (checks = []) => {
  const list = Array.isArray(checks) ? checks : [];
  const counts = {
    total: list.length,
    requested: 0,
    pending: 0,
    inProgress: 0,
    passed: 0,
    failed: 0,
    canceled: 0,
  };

  list.forEach((check) => {
    const status = String(check?.status || "").toUpperCase();
    if (status === "REQUESTED") counts.requested += 1;
    else if (status === "PENDING") counts.pending += 1;
    else if (status === "IN_PROGRESS") counts.inProgress += 1;
    else if (status === "PASSED") counts.passed += 1;
    else if (status === "FAILED") counts.failed += 1;
    else if (status === "CANCELED") counts.canceled += 1;
  });

  counts.open = counts.requested + counts.pending + counts.inProgress;
  counts.closed = counts.passed + counts.failed + counts.canceled;
  counts.passRate =
    counts.closed > 0 ? Math.round((counts.passed / counts.closed) * 100) : 0;
  return counts;
};

export default function BackgroundCheckDetailsPage() {
  const navigate = useNavigate();
  const { backgroundCheckId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const actorId = user?.id || "";
  const seededCheck = location.state?.check || null;
  const seededProfile = location.state?.profile || null;
  const seededHelperProfile = location.state?.helperProfile || location.state?.profile?.helperProfile || null;
  const seededUserEmail =
    seededHelperProfile?.email ||
    seededHelperProfile?.userEmail ||
    location.state?.check?.userEmail ||
    "";

  const { update, initiate, cancel, submitItemResult } = useBackgroundChecks();
  const { getUserById, searchUserProfiles } = useUserManagement(0, 20, null, true, { autoFetch: false });

  // Temporary diagnostics for empty detail screens.
  // eslint-disable-next-line no-console
  console.log("[BackgroundCheckDetails] route-state", {
    backgroundCheckId,
    hasCheckState: Boolean(location.state?.check),
    hasProfileState: Boolean(location.state?.profile),
    hasHelperProfileState: Boolean(location.state?.helperProfile || location.state?.profile?.helperProfile),
    seededCheckId: location.state?.check?.id || null,
    seededProfileId: location.state?.profile?.id || null,
    helperProfileId: location.state?.helperProfile?.id || location.state?.profile?.helperProfile?.id || null,
    helperProfileName:
      location.state?.helperProfile?.fullName ||
      [location.state?.helperProfile?.firstName, location.state?.helperProfile?.middleName, location.state?.helperProfile?.lastName]
        .filter(Boolean)
        .join(" ") ||
      location.state?.helperProfile?.userFullName ||
      null,
    helperProfileEmail:
      location.state?.helperProfile?.email ||
      location.state?.helperProfile?.userEmail ||
      null,
  });

  const [loading, setLoading] = useState(false);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [check, setCheck] = useState(seededCheck);
  const [profile, setProfile] = useState(seededProfile);
  const [activeUserDetails, setActiveUserDetails] = useState(null);
  const [error, setError] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [itemSubmitting, setItemSubmitting] = useState(false);
  const [itemSubmittingAction, setItemSubmittingAction] = useState(null);
  const [sectionReviewStates, setSectionReviewStates] = useState({});
  const [selectedItemKey, setSelectedItemKey] = useState(null);
  const [editForm] = Form.useForm();
  const [itemForm] = Form.useForm();

  const loadDetails = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!backgroundCheckId) return;
    // eslint-disable-next-line no-console
    console.log("[BackgroundCheckDetails] loadDetails:start", {
      backgroundCheckId,
      forceRefresh,
      hasSeededCheck: Boolean(seededCheck),
      hasSeededProfile: Boolean(seededProfile),
    });
    if (
      !forceRefresh &&
      seededCheck &&
      seededProfile &&
      Array.isArray(seededCheck?.verificationItems)
    ) {
      // eslint-disable-next-line no-console
      console.log("[BackgroundCheckDetails] using seeded check/profile", {
        checkId: seededCheck?.id || null,
        profileId: seededProfile?.id || null,
      });
      setCheck(seededCheck || null);
      setProfile(seededProfile || null);
      setError(null);
      setLoading(false);
      if (seededCheck) {
        editForm.setFieldsValue({
          checkType: seededCheck?.checkType,
          status: seededCheck?.status,
          notes: seededCheck?.notes || "",
        });
      }
      // Route state is only an immediate visual seed. Continue below and
      // refresh from the API so status and initiation timestamps are current.
    }
    setLoading(true);
    setError(null);
    try {
      const details = await olreadiaService.backgroundChecks.get(backgroundCheckId);
      // eslint-disable-next-line no-console
      console.log("[BackgroundCheckDetails] fetched background check", details);
      setCheck(details || null);
      editForm.setFieldsValue({
        checkType: details?.checkType,
        status: details?.status,
        notes: details?.notes || "",
      });

      const profileId =
        details?.professionProfileId || details?.professionProfile?.id;
      if (profileId) {
        // eslint-disable-next-line no-console
        console.log("[BackgroundCheckDetails] resolving profile", {
          profileId,
          seededProfileId: seededProfile?.id || null,
        });
        const profileRes =
          seededProfile?.id && String(seededProfile.id) === String(profileId)
            ? seededProfile
            : details?.profileName
              ? {
                  id: profileId,
                  profileName: details.profileName,
                  userFullName: details.userFullName,
                  sector: details.sectorName ? { name: details.sectorName } : null,
                }
              : await olreadiaService.professionProfiles.get(profileId);
        // eslint-disable-next-line no-console
        console.log("[BackgroundCheckDetails] resolved profile", profileRes);
        setProfile(profileRes || null);
      } else {
        // eslint-disable-next-line no-console
        console.log("[BackgroundCheckDetails] no profileId on check; keeping seededProfile", seededProfile);
        setProfile(seededProfile);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[BackgroundCheckDetails] loadDetails:error", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load background check details",
      );
      setCheck(null);
    } finally {
      setLoading(false);
    }
  }, [backgroundCheckId, editForm, seededCheck, seededProfile]);

  const syncDetails = useCallback(async () => {
    await loadDetails({ forceRefresh: true });
  }, [loadDetails]);

  useEffect(() => {
    loadDetails();
    return undefined;
  }, [loadDetails]);

  const activeCheck = check;
  const checkStats = useMemo(
    () => countChecksByStatus(activeCheck ? [activeCheck] : []),
    [activeCheck],
  );
  const checkStatus = String(activeCheck?.status || "").toUpperCase();
  const isCheckInitiated = Boolean(activeCheck?.initiatedAt) || ["IN_PROGRESS", "PASSED", "FAILED", "CANCELED"].includes(checkStatus);
  const verificationItems = useMemo(
    () =>
      activeCheck?.verificationItems ||
      activeCheck?.verification_items ||
      activeCheck?.items ||
      [],
    [activeCheck?.verificationItems, activeCheck?.verification_items, activeCheck?.items],
  );
  const verificationStats = useMemo(() => {
    const items = Array.isArray(verificationItems) ? verificationItems : [];
    const completed = items.filter((item) => {
      const status = String(item?.status || "").toUpperCase();
      const foundValue = getVerificationItemFoundValue(item);
      return Boolean(
        foundValue ||
          status === "PASS" ||
          status === "FAIL" ||
          status === "PASSED" ||
          status === "FAILED",
      );
    }).length;
    const pending = Math.max(items.length - completed, 0);
    return {
      total: items.length,
      completed,
      pending,
      progress: items.length ? Math.round((completed / items.length) * 100) : 0,
    };
  }, [verificationItems]);
  const selectedVerificationItem = useMemo(() => {
    if (!selectedItemKey) return null;
    return (
      verificationItems.find(
        (item, index) => getVerificationItemKey(item, index) === selectedItemKey,
      ) || null
    );
  }, [selectedItemKey, verificationItems]);
  const verificationSections = useMemo(() => {
    const grouped = new Map();
    verificationItems.forEach((item, index) => {
      const name = getVerificationCategory(item);
      if (!grouped.has(name)) grouped.set(name, []);
      grouped.get(name).push({ item, index });
    });
    return Array.from(grouped, ([name, entries]) => ({
      name,
      entries,
      pending: entries.some(({ item }) => getVerificationItemStateLabel(item) === "Pending"),
      failed: entries.some(({ item }) => getVerificationItemStateLabel(item) === "Failed"),
      complete: entries.every(({ item }) => ["Verified", "Captured"].includes(getVerificationItemStateLabel(item))),
    }));
  }, [verificationItems]);

  useEffect(() => {
    if (!verificationItems.length) {
      setSelectedItemKey(null);
      return;
    }

    if (!selectedItemKey) return;

    const activeIndex = verificationItems.findIndex(
      (item, index) => getVerificationItemKey(item, index) === selectedItemKey,
    );

    if (activeIndex === -1) {
      setSelectedItemKey(null);
      return;
    }

    const currentItem = verificationItems[activeIndex];
    itemForm.setFieldsValue(buildVerificationItemFormValues(currentItem));
  }, [itemForm, selectedItemKey, verificationItems]);

  const profileName = activeCheck?.profileName || profile?.profileName || profile?.userFullName || "-";
  const profileGroup = profile?.profileType || "GENERAL";
  const sectorName =
    activeCheck?.sectorName ||
    profile?.sector?.name ||
    profile?.sector?.code ||
    profile?.sectorCode ||
    "-";

  const checkUserName = activeCheck?.userFullName || activeUserDetails?.fullName || "-";
  const checkUserEmail = activeCheck?.userEmail || activeUserDetails?.email || "-";
  const checkUserPhone = activeCheck?.userPhone || activeUserDetails?.phone || "-";
  const isStatusEditable = [
    "IN_PROGRESS",
    "PASSED",
    "FAILED",
    "CANCELED",
  ].includes(checkStatus);

  // eslint-disable-next-line no-console
  console.log("[BackgroundCheckDetails] render", {
    activeCheckId: activeCheck?.id || null,
    profileId: profile?.id || null,
    profileName,
    checkUserName,
    checkUserEmail,
    checkUserPhone,
  });

  useEffect(() => {
    const activeUserId = activeCheck?.userId || activeCheck?.helperId || "";
    if (activeCheck?.userFullName) {
      setActiveUserDetails(null);
      setUserDetailsLoading(false);
      return undefined;
    }
    if (!activeUserId) {
      setActiveUserDetails(null);
      return undefined;
    }
    // eslint-disable-next-line no-console
    console.log("[BackgroundCheckDetails] fetch user lookup", { activeUserId });

    let cancelled = false;

    const loadUserDetails = async () => {
      try {
        setUserDetailsLoading(true);

        if (seededUserEmail) {
          const emailMatches = await searchUserProfiles(seededUserEmail.trim());
          const exactEmailMatch = (emailMatches || []).find((entry) => {
            const entryEmail = String(entry?.email || entry?.userEmail || "").toLowerCase();
            return entryEmail && entryEmail === seededUserEmail.trim().toLowerCase();
          });
          if (exactEmailMatch && !cancelled) {
            setActiveUserDetails(exactEmailMatch);
          }
        }

        const details = await getUserById(activeUserId);
        // eslint-disable-next-line no-console
        console.log("[BackgroundCheckDetails] user lookup result", details);
        if (!cancelled) {
          setActiveUserDetails(details || null);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[BackgroundCheckDetails] user lookup error", err);
        if (!cancelled) {
          setActiveUserDetails(null);
        }
      } finally {
        if (!cancelled) {
          setUserDetailsLoading(false);
        }
      }
    };

    loadUserDetails();

    return () => {
      cancelled = true;
    };
  }, [
    activeCheck?.helperId,
    activeCheck?.userEmail,
    activeCheck?.userFullName,
    activeCheck?.userId,
    activeCheck?.userPhone,
    getUserById,
    searchUserProfiles,
    seededUserEmail,
  ]);

  useEffect(() => {
    if (!activeCheck) return;
    editForm.setFieldsValue({
      checkType: activeCheck.checkType,
      status: activeCheck.status,
      notes: activeCheck.notes || "",
    });
  }, [activeCheck, editForm]);

  const openEditDrawer = useCallback(() => {
    if (!activeCheck) return;
    editForm.setFieldsValue({
      checkType: activeCheck.checkType,
      status: activeCheck.status,
      notes: activeCheck.notes || "",
    });
    setEditDrawerOpen(true);
  }, [activeCheck, editForm]);

  const closeEditDrawer = () => {
    setEditDrawerOpen(false);
  };

  const handleBack = () => {
    navigate(-1);
  };


  const handleGoToProfile = () => {
    if (!profile?.id) return;
    navigate(paths.helperProfessionProfileDetails(profile.id), {
      state: { profile },
    });
  };

  const handleSaveCheck = async () => {
    if (!activeCheck?.id) return;
    setSaving(true);
    try {
      const values = await editForm.validateFields();
      await update(
        activeCheck.id,
        {
          checkType: values.checkType,
          status: values.status,
          notes: values.notes || null,
        },
        actorId,
      );
      setEditDrawerOpen(false);
      await syncDetails();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.message || "Failed to update background check");
    } finally {
      setSaving(false);
    }
  };

  const handleInitiateCheck = async () => {
    if (!activeCheck?.id) return;
    setSelectedItemKey(null);
    setSaving(true);
    try {
      await initiate(activeCheck.id, actorId);
      await syncDetails();
      setSelectedItemKey(null);
    } catch (err) {
      message.error(err?.message || "Failed to initiate background check");
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteAndPass = () => {
    if (!activeCheck?.id || checkStatus !== "IN_PROGRESS") return;
    const remainingFields = Math.max(verificationStats.total - verificationStats.completed, 0);
    Modal.confirm({
      title: "Complete and pass this background check?",
      content: remainingFields
        ? `${remainingFields} verification field${remainingFields === 1 ? " is" : "s are"} still unfinished. You can still complete and pass the check.`
        : "All verification fields have been reviewed. This will mark the check as passed.",
      okText: "Complete & pass",
      cancelText: "Keep reviewing",
      onOk: async () => {
        setSaving(true);
        try {
          setSelectedItemKey(null);
          await update(
            activeCheck.id,
            {
              status: "PASSED",
              notes: activeCheck.notes || null,
            },
            actorId,
          );
          await syncDetails();
          message.success("Background check completed and passed");
        } catch (err) {
          message.error(err?.message || "Failed to complete background check");
          throw err;
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const handleCancelCheck = useCallback(() => {
    if (!activeCheck?.id) return;
    Modal.confirm({
      title: "Cancel background check?",
      content: "This will mark the background check as canceled.",
      okText: "Cancel check",
      okButtonProps: { danger: true },
      onOk: async () => {
        await cancel(activeCheck.id, actorId);
        await syncDetails();
      },
    });
  }, [activeCheck?.id, actorId, cancel, syncDetails]);

  const pageActionsMenu = useMemo(
    () => ({
      items: [
        {
          key: "edit",
          label: "Edit",
          onClick: openEditDrawer,
        },
        ...(String(activeCheck?.status || "").toUpperCase() !== "CANCELED"
          ? [
              {
                key: "cancel",
                label: "Cancel",
                danger: true,
                icon: <CloseOutlined />,
                onClick: handleCancelCheck,
              },
            ]
          : []),
      ],
    }),
    [activeCheck?.status, handleCancelCheck, openEditDrawer],
  );

  const openVerificationItem = (record = null, index = null) => {
    if (!isCheckInitiated) {
      message.info("Initiate the background check to unlock snapshots and verification items.");
      return;
    }
    const nextKey = getVerificationItemKey(record, index);
    setSelectedItemKey(nextKey);
    itemForm.setFieldsValue(buildVerificationItemFormValues(record));
  };

  const handleSaveItem = async (action = null) => {
    if (!activeCheck?.id) return;
    if (!selectedVerificationItem) {
      message.info("Select a verification item on the left first.");
      return;
    }
    const reviewedCategory = getVerificationCategory(selectedVerificationItem);
    setItemSubmitting(true);
    setItemSubmittingAction(action);
    try {
      if (action === "mismatch") {
        itemForm.setFieldsValue({ matchResult: "MISMATCH", status: "FAIL" });
      } else if (action === "verified") {
        itemForm.setFieldsValue({ matchResult: "TOTAL_MATCH", status: "PASS" });
      }
      const values = await itemForm.validateFields();
      await submitItemResult(
        activeCheck.id,
        {
          itemType: selectedVerificationItem?.itemType || values.itemType || null,
          propertyPath: selectedVerificationItem?.propertyPath || values.propertyPath || null,
          currentItem:
            normalizeSnapshotPayload(
              getVerificationItemSnapshotSource(selectedVerificationItem) ||
                selectedVerificationItem?.currentItem ||
                values.currentItem ||
                null,
            ),
          valueFound: toStableFileValue(values.valueFound),
          matchResult: values.matchResult || null,
          status: values.status || null,
          required: Boolean(values.required),
        },
        actorId,
      );
      await syncDetails();
      if (action) {
        setSectionReviewStates((current) => ({
          ...current,
          [reviewedCategory]: action,
        }));
      }
      setSelectedItemKey(null);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.message || "Failed to save verification item");
    } finally {
      setItemSubmitting(false);
      setItemSubmittingAction(null);
    }
  };

  if (loading && !activeCheck) {
    return (
      <div className="container-fluid p-2" style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div
          style={{
            minHeight: 280,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-2" style={{ maxWidth: 1240, margin: "0 auto" }}>
      {error ? (
        <Card size="small" style={{ borderRadius: 12, marginBottom: 16 }} styles={{ body: { padding: 12 } }}>
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Text strong>Failed to load background check</Text>
            <Text type="secondary">{error}</Text>
            <div>
              <Button onClick={loadDetails}>Retry</Button>
            </div>
          </Space>
        </Card>
      ) : null}

      {activeCheck ? (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              padding: "2px 0 4px",
            }}
          >
            <div style={{ minWidth: 0, flex: "1 1 320px" }}>
              <WidgetSectionTitle
                icon="check"
                transform="shrink-2"
                title="Background Check"
                subtitle={activeCheck ? toLabel(activeCheck.checkType) : "Workflow details"}
                className="mb-0"
              />
            </div>
            <Space wrap size={8} style={{ justifyContent: "flex-end" }}>
              {loading || userDetailsLoading ? <Spin size="small" /> : null}
              <Button onClick={handleBack}>
                Go Back
              </Button>
              {profile?.id ? (
                <Button onClick={handleGoToProfile}>
                  Profession Profile
                </Button>
              ) : null}
              <Dropdown menu={pageActionsMenu} trigger={["click"]}>
                <Button>
                  Actions <EllipsisOutlined />
                </Button>
              </Dropdown>
            </Space>
          </div>
          <div>
            <Text strong style={{ color: "#8c8c86", fontSize: 13, letterSpacing: 0.3 }}>
              1 · OVERVIEW
            </Text>
          </div>
          <Card
            style={{ borderRadius: 12, borderColor: "#e5e7eb" }}
            styles={{ body: { padding: "12px 16px" } }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 20,
                flexWrap: "wrap",
              }}
            >
              <Space size={12} align="center">
                <Avatar size={40} style={{ background: "#dbeafe", color: "#1d4f91", fontWeight: 600 }}>
                  {String(checkUserName || profileName || "BC")
                    .split(" ")
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((part) => part[0])
                    .join("")
                    .toUpperCase()}
                </Avatar>
                <div>
                  <Text strong style={{ display: "block", color: "#171717", fontSize: 16 }}>
                    {checkUserName || profileName}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {[profileName, sectorName].filter(Boolean).join(" · ")}
                  </Text>
                </div>
              </Space>

              {!isCheckInitiated ? (
                <Button type="primary" size="large" onClick={handleInitiateCheck} loading={saving}>
                  Initiate check
                </Button>
              ) : (
                <Space wrap align="center" size={16}>
                  <Tag
                    color={checkStatus === "PASSED" ? "green" : checkStatus === "FAILED" ? "red" : "blue"}
                    style={{ marginInlineEnd: 0, paddingInline: 9, lineHeight: "20px", fontSize: 11 }}
                  >
                    {toLabel(activeCheck.status)}
                  </Tag>
                  {checkStatus === "IN_PROGRESS" ? (
                    <Button
                      onClick={handleCompleteAndPass}
                      loading={saving}
                      style={{
                        minWidth: 124,
                        height: 32,
                        color: "#fff",
                        background: "#16a34a",
                        borderColor: "#16a34a",
                        boxShadow: "none",
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      Complete & pass
                    </Button>
                  ) : null}
                </Space>
              )}
            </div>
            <Divider style={{ margin: "10px 0 8px" }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {isCheckInitiated
                ? `${verificationStats.completed} of ${verificationStats.total} verification fields reviewed`
                : "Not started · sections unlock once the check is initiated"}
            </Text>
          </Card>
          <Row gutter={[8, 8]} style={{ display: "none" }}>
            <Col xs={24} lg={10}>
              <Card
                size="small"
                title="Person & profession profile"
                style={{ borderRadius: 14, height: "100%", borderColor: "#e2e8f0" }}
                styles={{ header: { background: "#f8fafc" }, body: { padding: 16 } }}
              >
                <div style={{ display: "grid", gap: 8 }}>
                  <div>
                    <Text style={{ fontSize: 13, fontWeight: 500, color: "#1f2937" }}>
                      {profileName}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
                        {profileGroup} {sectorName ? `• ${sectorName}` : ""}
                      </Text>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <div>
                      <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0.2 }}>
                        Name
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 12.5, fontWeight: 500, color: "#111827" }}>{checkUserName}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0.2 }}>
                        Email
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 12.5, fontWeight: 500, color: "#111827" }}>{checkUserEmail}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0.2 }}>
                        Phone
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 12.5, fontWeight: 500, color: "#111827" }}>{checkUserPhone}</Text>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0.2 }}>Status</Text>
                    <div style={{ marginTop: 6 }}>
                      <Space size={8} wrap align="center">
                        <Tag
                          style={{
                            marginInlineEnd: 0,
                            borderColor: "#d1d5db",
                            background: "#f9fafb",
                            fontWeight: 500,
                          }}
                        >
                          {toLabel(activeCheck.status)}
                        </Tag>
                        {isStatusEditable ? (
                          <Button
                            size="small"
                            type="text"
                            icon={<EditOutlined />}
                            onClick={openEditDrawer}
                            style={{
                              color: "#2563eb",
                              background: "#eff6ff",
                              border: "1px solid #bfdbfe",
                              borderRadius: 999,
                            }}
                            aria-label="Edit background check status"
                          />
                        ) : null}
                        {String(activeCheck.status || "").toUpperCase() === "REQUESTED" ||
                        String(activeCheck.status || "").toUpperCase() === "PENDING" ? (
                          <Button
                            size="small"
                            onClick={handleInitiateCheck}
                            disabled={saving}
                          >
                            {saving ? "Loading..." : "Initiate"}
                          </Button>
                        ) : null}
                      </Space>
                    </div>
                  </div>

                  <div>
                    <Text type="secondary" style={{ fontSize: 10, fontWeight: 400, letterSpacing: 0.2 }}>Flow</Text>
                    <div style={{ marginTop: 4 }}>
                      <Text style={{ fontSize: 12.5, fontWeight: 500, color: "#111827" }}>{checkStats.closed} closed</Text>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={14}>
              <Card
                size="small"
                title="Check timeline & references"
                style={{ borderRadius: 14, height: "100%", borderColor: "#e2e8f0" }}
                styles={{ header: { background: "#f8fafc" }, body: { padding: 16 } }}
              >
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, letterSpacing: 0.2, fontWeight: 600 }}
                  >
                    Check information
                  </Text>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: 10,
                    }}
                  >
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>ID</Text>
                      <div style={{ marginTop: 2 }}>
                        <CopyableText
                          value={activeCheck.id}
                          showTooltip={false}
                          stopPropagation={false}
                        />
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Type</Text>
                      <div style={{ marginTop: 2 }}>
                        <Text strong style={{ fontSize: 12 }}>{toLabel(activeCheck.checkType)}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Requested</Text>
                      <div style={{ marginTop: 2 }}>
                        <Text style={{ fontSize: 12 }}>{formatDateTime(activeCheck.requestedAt)}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>By</Text>
                      <div style={{ marginTop: 2 }}>
                        <CopyableText
                          value={activeCheck.requestedBy}
                          showTooltip={false}
                          stopPropagation={false}
                        />
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Started</Text>
                      <div style={{ marginTop: 2 }}>
                        <Text style={{ fontSize: 12 }}>{formatDateTime(activeCheck.initiatedAt)}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Started by</Text>
                      <div style={{ marginTop: 2 }}>
                        <CopyableText
                          value={activeCheck.initiatedBy}
                          showTooltip={false}
                          stopPropagation={false}
                        />
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Done</Text>
                      <div style={{ marginTop: 2 }}>
                        <Text style={{ fontSize: 12 }}>{formatDateTime(activeCheck.completedAt)}</Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 10 }}>User</Text>
                      <div style={{ marginTop: 2 }}>
                        <CopyableText
                          value={activeCheck.userId}
                          showTooltip={false}
                          stopPropagation={false}
                        />
                      </div>
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <Text type="secondary" style={{ fontSize: 10 }}>Notes</Text>
                      <div style={{ marginTop: 2 }}>
                        <Text style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
                          {activeCheck.notes || "-"}
                        </Text>
                      </div>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
          <div style={{ marginTop: 8 }}>
            <Text strong style={{ color: "#8c8c86", fontSize: 12, letterSpacing: 0.2 }}>
              2 · AFTER INITIATING — SECTIONS TO REVIEW
            </Text>
          </div>

          <Card
            style={{ borderRadius: 12, borderColor: "#e5e7eb", overflow: "hidden" }}
            styles={{ body: { padding: 0 } }}
          >
            {!isCheckInitiated ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <Text type="secondary">Sections unlock once the background check is initiated.</Text>
              </div>
            ) : verificationSections.length ? (
              verificationSections.map((section, sectionIndex) => {
                const firstEntry = section.entries[0];
                const savedSectionState = sectionReviewStates[section.name];
                const sectionFailed = savedSectionState === "mismatch" || (!savedSectionState && section.failed);
                const sectionComplete = savedSectionState === "verified" || (!savedSectionState && section.complete);
                const selectedCategory = selectedVerificationItem
                  ? getVerificationCategory(selectedVerificationItem)
                  : null;
                const isSelected = selectedCategory === section.name;
                return (
                  <button
                    key={section.name}
                    type="button"
                    onClick={() => openVerificationItem(firstEntry.item, firstEntry.index)}
                    style={{
                      width: "100%",
                      minHeight: 48,
                      padding: "0 14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      border: "none",
                      borderBottom: sectionIndex < verificationSections.length - 1 ? "1px solid #e5e7eb" : "none",
                      background: sectionComplete ? "#f0fdf4" : sectionFailed ? "#fff7ed" : isSelected ? "#f8fbff" : "#fff",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <Space size={9}>
                      <Text style={{ color: "#595959", fontSize: 15, lineHeight: 1 }}>{getSectionIcon(section.name)}</Text>
                      <Text strong style={{ color: "#171717", fontSize: 14 }}>{section.name}</Text>
                      <Tag
                        color={sectionFailed ? "red" : sectionComplete ? "green" : "gold"}
                        bordered={false}
                        style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "20px", paddingInline: 8 }}
                      >
                        {sectionFailed ? "Needs attention" : sectionComplete ? "Verified" : "Pending"}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>{section.entries.length} fields</Text>
                    </Space>
                    <RightOutlined style={{ color: "#8c8c86", fontSize: 11 }} />
                  </button>
                );
              })
            ) : (
              <Empty description="No verification sections available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          {false && selectedVerificationItem && isCheckInitiated ? (
            <div style={{ marginTop: 12 }}>
              <Text strong style={{ color: "#8c8c86", fontSize: 13, letterSpacing: 0.3 }}>
                3 · REVIEW {getVerificationCategory(selectedVerificationItem).toUpperCase()}
              </Text>
              <Card
                style={{ marginTop: 10, borderRadius: 16, borderColor: "#e5e7eb" }}
                styles={{ body: { padding: 24 } }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 20 }}>
                  <Space size={12}>
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => setSelectedItemKey(null)} />
                    <Text strong style={{ fontSize: 17 }}>{getVerificationCategory(selectedVerificationItem)}</Text>
                  </Space>
                  <Tag color={getVerificationItemStateColor(selectedVerificationItem)} bordered={false}>
                    {getVerificationItemStateLabel(selectedVerificationItem)}
                  </Tag>
                </div>

                <Row gutter={[20, 20]}>
                  <Col xs={24} lg={12}>
                    <Card
                      size="small"
                      title="Backend snapshot"
                      style={{ borderRadius: 12, height: "100%", borderColor: "#e5e7eb" }}
                      styles={{ body: { padding: 16 } }}
                    >
                      {renderStructuredSnapshotValue(getVerificationItemSnapshotSource(selectedVerificationItem))}
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card
                      size="small"
                      title="Verification evidence"
                      style={{ borderRadius: 12, height: "100%", borderStyle: "dashed", borderColor: "#cbd5e1" }}
                      styles={{ body: { padding: 16 } }}
                    >
                      <Form layout="vertical" form={itemForm} initialValues={{ required: true }}>
                        <Form.Item name="itemType" hidden><Input /></Form.Item>
                        <Form.Item name="propertyPath" hidden><Input /></Form.Item>
                        <Form.Item name="currentItem" hidden><Input /></Form.Item>
                        <Form.Item name="matchResult" hidden><Input /></Form.Item>
                        <Form.Item name="status" hidden><Input /></Form.Item>
                        <Form.Item
                          name="valueFound"
                          label="Evidence or verified value"
                          extra="Add a concise note from documents, calls, or other evidence."
                        >
                          <Input.TextArea autoSize={{ minRows: 5, maxRows: 8 }} placeholder="No verification evidence yet" />
                        </Form.Item>
                      </Form>
                    </Card>
                  </Col>
                </Row>

                <Space wrap style={{ width: "100%", justifyContent: "flex-end", marginTop: 20 }}>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    loading={itemSubmitting}
                    onClick={() => {
                      itemForm.setFieldsValue({ matchResult: "MISMATCH", status: "FAIL" });
                      handleSaveItem();
                    }}
                  >
                    Flag mismatch
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={itemSubmitting}
                    onClick={() => {
                      itemForm.setFieldsValue({ matchResult: "TOTAL_MATCH", status: "PASS" });
                      handleSaveItem();
                    }}
                  >
                    Confirm match
                  </Button>
                </Space>
              </Card>
            </div>
          ) : null}

          <Row gutter={[16, 16]} style={{ marginTop: 8, display: "none" }}>
            <Col xs={24}>
              <Card
                size="small"
                style={{
                  borderRadius: 14,
                  borderColor: "#e5e7eb",
                }}
                styles={{ body: { padding: 12 } }}
                title="Verification sections"
                extra={<Text type="secondary">{verificationStats.pending} pending</Text>}
              >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {verificationItems.length} fields organised for review
                    </Text>
                    <Tag>{verificationStats.progress}% complete</Tag>
                  </div>

                  <Divider style={{ margin: "4px 0 0" }} />

                  {verificationItems.length ? (
                    <List
                      dataSource={verificationItems}
                      split={false}
                      renderItem={(record, index) => {
                        const key = getVerificationItemKey(record, index);
                        const isSelected = selectedItemKey === key;
                        const stateLabel = getVerificationItemStateLabel(record);
                        const accent = getVerificationItemAccent(record);
                        const isVerified = stateLabel === "Verified";
                        const rowStyle = isSelected
                          ? {
                              borderColor: accent.borderColor,
                              background: accent.background,
                              boxShadow: accent.shadow,
                            }
                          : isVerified
                          ? {
                              borderColor: accent.borderColor,
                              background: accent.softBackground,
                              boxShadow: `0 1px 2px ${accent.borderColor}22`,
                            }
                          : {
                              borderColor: "#e2e8f0",
                              background: "#ffffff",
                              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
                            };

                        return (
                          <List.Item style={{ padding: 0, marginBottom: 10 }}>
                            <Row gutter={12} style={{ width: "100%" }} align="stretch">
                              <Col xs={24} xl={8}>
                                <div
                                  style={{
                                    width: "100%",
                                    border: "1px solid",
                                    ...rowStyle,
                                    borderRadius: 14,
                                    padding: 12,
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => openVerificationItem(record, index)}
                                    disabled={!isCheckInitiated}
                                    style={{
                                      width: "100%",
                                      textAlign: "left",
                                      border: "none",
                                      background: "transparent",
                                      padding: 0,
                                      cursor: isCheckInitiated ? "pointer" : "not-allowed",
                                      opacity: isCheckInitiated ? 1 : 0.7,
                                    }}
                                  >
                                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                                        <div style={{ minWidth: 0 }}>
                                          <Tag
                                            bordered={false}
                                            color="blue"
                                            style={{ marginBottom: 8, marginInlineEnd: 0 }}
                                          >
                                            {getVerificationCategory(record)}
                                          </Tag>
                                          <div />
                                          <Text strong style={{ fontSize: 13, color: "#0f172a" }}>
                                            {index + 1}. {formatFieldLabel(record?.propertyPath || record?.itemType)}
                                          </Text>
                                          <div style={{ marginTop: 4 }}>
                                            <Text type="secondary" style={{ fontSize: 11, wordBreak: "break-word" }}>
                                              {toLabel(record?.itemType)}
                                            </Text>
                                          </div>
                                        </div>
                                        <Space wrap size={6}>
                                          <Tag
                                            color={getVerificationItemStateColor(record)}
                                            icon={getVerificationItemStateLabel(record) === "Verified" ? <CheckOutlined /> : null}
                                          >
                                            {stateLabel}
                                          </Tag>
                                          {record?.required ? <Tag>Required</Tag> : <Tag>Optional</Tag>}
                                        </Space>
                                      </div>
                                    </Space>
                                  </button>

                                  {isSelected && isCheckInitiated ? (
                                    <div
                                      style={{
                                        marginTop: 12,
                                        borderRadius: 12,
                                        padding: 10,
                                        background: accent.softBackground,
                                        border: `1px solid ${accent.softBorder}`,
                                      }}
                                    >
                                      <Text type="secondary" style={{ fontSize: 11, fontWeight: 600 }}>
                                        Profile data snapshot
                                      </Text>
                                      <div style={{ marginTop: 6 }}>
                                        {renderStructuredSnapshotValue(
                                          getVerificationItemSnapshotSource(record),
                                        )}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </Col>

                              <Col xs={24} xl={16}>
                                {isSelected && isCheckInitiated ? (
                                  <Card
                                    size="small"
                                    style={{
                                      borderRadius: 14,
                                      borderColor: accent.borderColor,
                                      background: accent.background,
                                      boxShadow: accent.shadow,
                                    }}
                                    styles={{ body: { padding: 12, background: `linear-gradient(180deg, ${accent.background} 0%, #fff 100%)` } }}
                                    title={`Review ${getVerificationCategory(record)}`}
                                    extra={<Text type="secondary">Verify against calls, documents, or other evidence</Text>}
                                  >
                                    <Form layout="vertical" form={itemForm} initialValues={{ required: true }}>
                                      <Form.Item name="itemType" hidden>
                                        <Input />
                                      </Form.Item>
                                      <Form.Item name="propertyPath" hidden>
                                        <Input />
                                      </Form.Item>
                                      <Form.Item name="currentItem" hidden>
                                        <Input />
                                      </Form.Item>

                                      <Form.Item
                                        name="valueFound"
                                        label="Found value"
                                        extra="Enter what you verified from calls, documents, or other evidence."
                                      >
                                        <Input.TextArea
                                          placeholder="Observed value"
                                          autoSize={{ minRows: 4, maxRows: 8 }}
                                        />
                                      </Form.Item>

                                      <Row gutter={12}>
                                        <Col xs={24} md={12}>
                                          <Form.Item name="matchResult" label="Match">
                                            <Select
                                              allowClear
                                              placeholder="Select"
                                              options={[
                                                { value: "TOTAL_MATCH", label: "Total match" },
                                                { value: "PARTIAL_MATCH", label: "Partial match" },
                                                { value: "MISMATCH", label: "Mismatch" },
                                              ]}
                                            />
                                          </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                          <Form.Item name="status" label="Status">
                                            <Select
                                              allowClear
                                              placeholder="Select"
                                              options={[
                                                { value: "PASS", label: "Pass" },
                                                { value: "FAIL", label: "Fail" },
                                              ]}
                                            />
                                          </Form.Item>
                                        </Col>
                                      </Row>

                                      <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                                        <Button onClick={handleSaveItem} loading={itemSubmitting} type="primary">
                                          Save
                                        </Button>
                                      </Space>
                                    </Form>
                                  </Card>
                                ) : (
                                  <div
                                    style={{
                                      minHeight: 64,
                                      borderRadius: 14,
                                      border: "1px dashed #e5e7eb",
                                      background: "#fff",
                                    }}
                                  />
                                )}
                              </Col>
                            </Row>
                          </List.Item>
                        );
                      }}
                      locale={{
                        emptyText: (
                          <Empty
                            description="No verification data is available for this check."
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          />
                        ),
                      }}
                    />
                  ) : (
                    <Empty
                      description="No verification data is available for this check."
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </Space>
              </Card>
            </Col>
          </Row>
        </Space>
      ) : null}

      <Drawer
        open={Boolean(selectedVerificationItem && isCheckInitiated)}
        title={selectedVerificationItem ? `Review ${getVerificationCategory(selectedVerificationItem)}` : "Review section"}
        width={720}
        zIndex={1450}
        onClose={() => setSelectedItemKey(null)}
        destroyOnHidden
        styles={{ body: { padding: 20 }, footer: { padding: "14px 20px" } }}
        extra={selectedVerificationItem ? (
          <Tag color={getVerificationItemStateColor(selectedVerificationItem)} bordered={false}>
            {getVerificationItemStateLabel(selectedVerificationItem)}
          </Tag>
        ) : null}
        footer={
          <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => setSelectedItemKey(null)}>Close</Button>
            <Button
              danger
              icon={<CloseOutlined />}
              loading={itemSubmittingAction === "mismatch"}
              disabled={itemSubmitting && itemSubmittingAction !== "mismatch"}
              onClick={() => handleSaveItem("mismatch")}
            >
              Flag mismatch
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={itemSubmittingAction === "verified"}
              disabled={itemSubmitting && itemSubmittingAction !== "verified"}
              onClick={() => handleSaveItem("verified")}
            >
              Confirm match
            </Button>
          </Space>
        }
      >
        {selectedVerificationItem ? (
          <Space direction="vertical" size={18} style={{ width: "100%" }}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Field being reviewed</Text>
              <Text strong style={{ display: "block", marginTop: 4, fontSize: 16 }}>
                {formatFieldLabel(selectedVerificationItem?.propertyPath || selectedVerificationItem?.itemType)}
              </Text>
            </div>

            <Card
              size="small"
              title="Backend snapshot"
              style={{ borderRadius: 12, borderColor: "#e5e7eb" }}
              styles={{ header: { background: "#f8fafc" }, body: { padding: 16 } }}
            >
              <FileEvidence
                fileId={selectedVerificationItem.currentItemValue}
                signedUrl={selectedVerificationItem.currentItemUrl}
                image={/photo|image/i.test(String(selectedVerificationItem.itemType || ""))}
                label="Profile file"
              />
              {renderStructuredSnapshotValue(getVerificationItemSnapshotSource(selectedVerificationItem))}
            </Card>

            <Card
              size="small"
              title="Verification evidence"
              style={{ borderRadius: 12, borderStyle: "dashed", borderColor: "#cbd5e1" }}
              styles={{ body: { padding: 16 } }}
            >
              <FileEvidence
                fileId={selectedVerificationItem.valueFound}
                signedUrl={selectedVerificationItem.valueFoundUrl}
                image={/photo|image/i.test(String(selectedVerificationItem.itemType || ""))}
                label="Verification evidence file"
              />
              <Form layout="vertical" form={itemForm} initialValues={{ required: true }}>
                <Form.Item name="itemType" hidden><Input /></Form.Item>
                <Form.Item name="propertyPath" hidden><Input /></Form.Item>
                <Form.Item name="currentItem" hidden><Input /></Form.Item>
                <Form.Item name="matchResult" hidden><Input /></Form.Item>
                <Form.Item name="status" hidden><Input /></Form.Item>
                <Form.Item
                  name="valueFound"
                  label="Evidence or verified value"
                  extra="Add a concise note from documents, calls, or other evidence."
                  style={{ marginBottom: 0 }}
                >
                  <Input.TextArea
                    autoFocus
                    autoSize={{ minRows: 6, maxRows: 12 }}
                    placeholder="No verification evidence yet"
                  />
                </Form.Item>
              </Form>
            </Card>
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        open={editDrawerOpen}
        title="Edit check"
        width={720}
        zIndex={1500}
        onClose={closeEditDrawer}
        destroyOnHidden
        styles={{ body: { paddingBottom: 96 } }}
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={closeEditDrawer}>Cancel</Button>
            <Button onClick={handleSaveCheck} loading={saving}>
              Save changes
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Space direction="vertical" size={4}>
              <Text type="secondary">Check</Text>
              <Text strong>{toLabel(activeCheck?.checkType)}</Text>
              <Text type="secondary">
                {profile?.profileName || profile?.userFullName || "-"}
              </Text>
            </Space>
          </Card>

          <Form layout="vertical" form={editForm}>
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="checkType"
                  label="Check type"
                  rules={[
                    {
                      required: true,
                      message: "Select a background check type",
                    },
                  ]}
                >
                  <Select options={BACKGROUND_CHECK_TYPES} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: "Select a status" }]}
                >
                  <Select options={BACKGROUND_CHECK_STATUSES} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={3} placeholder="Optional admin notes" />
            </Form.Item>
          </Form>
        </Space>
      </Drawer>

    </div>
  );
}
